import { useEffect, useRef, useState } from "react";
import { Flag, MoreHorizontal, Slash } from "lucide-react";

import { blockUser, reportUser } from "../../Services/SocialService";
import "../../Styles/UserOverflowMenu.css";

/**
 * Blockera eller anmäl någon.
 *
 * Blockering kräver ett extra tryck. Den river följningarna åt båda håll
 * och går inte att ångra med samma knapp, så ett felklick ska inte räcka.
 *
 * Anmälan skickas och försvinner. Vi lovar inget om vad som händer sedan,
 * eftersom hanteringen är manuell — att låtsas om ett ärendeflöde som inte
 * finns vore värre än att säga som det är.
 */
type Props = {
  userId: string;
  displayName: string;
  /** Körs när blockeringen gått igenom, så vyn kan läsa om sig. */
  onBlocked: () => void;
};

const REPORT_REASONS = [
  "Spam",
  "Harassment",
  "Fake profile",
  "Inappropriate content",
  "Something else",
];

export default function UserOverflowMenu({ userId, displayName, onBlocked }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<"menu" | "block" | "report">("menu");
  const [reason, setReason] = useState(REPORT_REASONS[0]);
  const [details, setDetails] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Klick utanför stänger menyn. Utan det ligger den kvar över innehållet
  // när man navigerar vidare med musen.
  useEffect(() => {
    if (!isOpen) return;

    const onClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) close();
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isOpen]);

  const close = () => {
    setIsOpen(false);
    setMode("menu");
    setError(null);
    setDone(null);
    setDetails("");
  };

  const handleBlock = async () => {
    if (isBusy) return;

    setIsBusy(true);
    setError(null);

    try {
      await blockUser(userId);
      close();
      onBlocked();
    } catch (e) {
      console.error("block failed", e);
      setError("Could not block right now.");
    } finally {
      setIsBusy(false);
    }
  };

  const handleReport = async () => {
    if (isBusy) return;

    setIsBusy(true);
    setError(null);

    try {
      await reportUser({ userId, reason, details: details.trim() || undefined });
      setDone("Thanks — we'll take a look.");
      setMode("menu");
    } catch (e) {
      console.error("report failed", e);
      setError("Could not send the report.");
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="overflow-menu" ref={wrapperRef}>
      <button
        type="button"
        className="overflow-trigger"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={`More options for ${displayName}`}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
      >
        <MoreHorizontal size={18} strokeWidth={2.2} />
      </button>

      {isOpen && (
        <div className="overflow-panel" role="menu">
          {error && <p className="overflow-error">{error}</p>}
          {done && <p className="overflow-done">{done}</p>}

          {mode === "menu" && (
            <>
              <button
                type="button"
                role="menuitem"
                className="overflow-item"
                onClick={() => setMode("block")}
              >
                <Slash size={14} strokeWidth={2.2} />
                Block {displayName}
              </button>

              <button
                type="button"
                role="menuitem"
                className="overflow-item"
                onClick={() => setMode("report")}
              >
                <Flag size={14} strokeWidth={2.2} />
                Report
              </button>
            </>
          )}

          {mode === "block" && (
            <div className="overflow-confirm">
              <p>
                Block {displayName}? You will stop seeing each other, and any
                follows between you are removed.
              </p>

              <div className="overflow-actions">
                <button
                  type="button"
                  className="overflow-danger"
                  disabled={isBusy}
                  onClick={handleBlock}
                >
                  {isBusy ? "…" : "Block"}
                </button>

                <button
                  type="button"
                  className="overflow-cancel"
                  disabled={isBusy}
                  onClick={() => setMode("menu")}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {mode === "report" && (
            <div className="overflow-confirm">
              <label className="overflow-field">
                <span>Reason</span>
                <select value={reason} onChange={(e) => setReason(e.target.value)}>
                  {REPORT_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>

              <label className="overflow-field">
                <span>Anything to add? (optional)</span>
                <textarea
                  rows={3}
                  maxLength={1000}
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                />
              </label>

              <div className="overflow-actions">
                <button
                  type="button"
                  className="overflow-danger"
                  disabled={isBusy}
                  onClick={handleReport}
                >
                  {isBusy ? "…" : "Send report"}
                </button>

                <button
                  type="button"
                  className="overflow-cancel"
                  disabled={isBusy}
                  onClick={() => setMode("menu")}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
